
import React, { useState, useRef, useEffect } from "react";
import moment from "moment";

const EventCalendar = ({ onDateRangeChange }) => {
  const [isPickerVisible, setPickerVisible] = useState(false);
  const [pickerStyle, setPickerStyle] = useState({});
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState("");
  const [activeRange, setActiveRange] = useState("Last 7 Days");
  const dateRangeRef = useRef(null);
  const [currentStartMonth, setCurrentStartMonth] = useState(
    moment().startOf("month")
  );
  const [currentEndMonth, setCurrentEndMonth] = useState(
    moment().add(1, "month").startOf("month")
  );
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: null,
    end: null,
  });
  const [days, setDays] = useState([]);
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const predefinedRanges = [
    { label: "Today", range: [new Date(), new Date()] },
    {
      label: "Yesterday",
      range: [new Date(Date.now() - 864e5), new Date(Date.now() - 864e5)],
    },
    {
      label: "Last 7 Days",
      range: [new Date(Date.now() - 6 * 864e5), new Date()],
    },
    {
      label: "Last 30 Days",
      range: [new Date(Date.now() - 29 * 864e5), new Date()],
    },
    {
      label: "This Month",
      range: [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date(),
      ],
    },
    {
      label: "Last Month",
      range: [
        new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
        new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      ],
    },
    { label: "Custom Range", range: null },
  ];

  useEffect(() => {
    // Set default range to "Today" on component mount
    const today = new Date();
    const startDate = new Date(Date.now() - 6 * 864e5);
    const endDate = new Date();
    setStartDate(startDate);
    setEndDate(endDate);
    setSelectedRange(`${formatDate(startDate)} - ${formatDate(endDate)}`);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      let start_date = moment(startDate).format("YYYY-MM-DD");
      let end_date = moment(endDate).format("YYYY-MM-DD");
      onDateRangeChange(start_date, end_date);
    }
  }, [startDate, endDate, onDateRangeChange]);

  const toggleList = () => {
    let display = isPickerVisible ? "none" : "block";
    showRange(display);
    setPickerVisible(!isPickerVisible);
  };

  const showRange = (display) => {
    if (dateRangeRef.current) {
      const rect = dateRangeRef.current.getBoundingClientRect();
      let marginTop = rect.top + window.scrollY;
      marginTop = marginTop + 29;
      setPickerStyle({
        display: display,
        top: `${marginTop}px`,
        right: "60.9996px", // Adjust as needed
        left: "auto",
      });
    }
  };

  const handlePredefinedRange = (range, label) => {
    if (label === "Custom Range") {
      setShowCustomRange(true); // Show the calendar when "Custom Range" is selected
    } else {
      setShowCustomRange(false);
      setStartDate(range[0]);
      setEndDate(range[1]);
      //console.log(`${formatDate(range[0])} - ${formatDate(range[1])}`);
      setSelectedRange(`${formatDate(range[0])} - ${formatDate(range[1])}`);
      setPickerVisible(!isPickerVisible);
      showRange("none");
      //setDropdownOpen(false);
      //onDateRangeSelect(range); // Send the selected range back to the parent
    }
    setActiveRange(label);
  };

  const handleDateSelection = (day) => {
    const selectedDay = moment(day);

    // If no start date is selected yet
    if (!selectedDateRange.start) {
      setSelectedDateRange({ start: selectedDay, end: null });
    }
    // If start date is selected, set the end date or adjust start/end based on selection
    else if (!selectedDateRange.end) {
      if (selectedDay.isBefore(selectedDateRange.start)) {
        setSelectedDateRange({
          start: selectedDay,
          end: selectedDateRange.start,
        });
      } else {
        setSelectedDateRange({ ...selectedDateRange, end: selectedDay });
      }
    }
    // If both start and end dates are already selected, reset selection
    else {
      setSelectedDateRange({ start: selectedDay, end: null });
    }
  };

  const isDayInRange = (day) => {
    if (selectedDateRange.start && selectedDateRange.end) {
      return day.isBetween(
        selectedDateRange.start,
        selectedDateRange.end,
        "day",
        "[]"
      );
    }
    return false;
  };

  const handlePrevMonth = () => {
    setCurrentStartMonth((prev) => prev.clone().subtract(1, "month"));
    setCurrentEndMonth((prev) => prev.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentStartMonth((prev) => prev.clone().add(1, "month"));
    setCurrentEndMonth((prev) => prev.clone().add(1, "month"));
  };


  const renderCalendar = (id, monthYearId, date, setMonthFunc) => {
    const startOfMonth = moment(date).startOf("month");
    const endOfMonth = moment(date).endOf("month");
    const startOfWeek = startOfMonth.clone().startOf("week");
    const endOfWeek = endOfMonth.clone().endOf("week");

    let day = startOfWeek;
    const calendarDays = [];

    while (day.isBefore(endOfWeek)) {
      calendarDays.push(day.clone());
      day.add(1, "day");
    }

    return (
      <div className="calendar" id={id}>
        <div className="header">
          {monthYearId === "month-year-left" && (
            <button id="prev-month" className="prev available" onClick={() => handlePrevMonth(setMonthFunc)}>
              <span></span>
            </button>
          )}
          <span id={monthYearId} className="month-year">
            {moment(date).format("MMMM YYYY")}
          </span>
          {monthYearId === "month-year-right" && (
            <button id="next-month" className="next available" onClick={() => handleNextMonth(setMonthFunc)}>
              <span></span>
            </button>
          )}
        </div>
        <div className="days">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((dayName) => (
            <div key={dayName} className="day-name">
              {dayName}
            </div>
          ))}
        </div>
        <div className="days">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`day ${day.month() !== date.month() ? "inactive" : ""} 
                ${isDayInRange(day) ? "range" : ""} 
                ${
                  selectedDateRange.start &&
                  day.isSame(selectedDateRange.start, "day")
                    ? "selected-start"
                    : ""
                } 
                ${
                  selectedDateRange.end &&
                  day.isSame(selectedDateRange.end, "day")
                    ? "selected-end"
                    : ""
                }`}
              data-date={day.format("YYYY-MM-DD")}
              onClick={() => handleDateSelection(day)}
            >
              {day.date()}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleApply = () => {
    let start = selectedDateRange.start
      ? selectedDateRange.start.format("YYYY-MM-DD")
      : "";
    let end = selectedDateRange.end
      ? selectedDateRange.end.format("YYYY-MM-DD")
      : "";
    if (start && end) {
      start = new Date(start);
      end = new Date(end);
      setStartDate(start);
      setEndDate(end);
      //console.log(`${formatDate(start)} - ${formatDate(end)}`);
      setSelectedRange(`${formatDate(start)} - ${formatDate(end)}`);
      showRange('none');
      //console.log(`Selected Range: ${start} - ${end}`);
    }
  };

  return (
    <React.Fragment>
      <div
        className="cx-date-range"
        title="Date"
        onClick={() => toggleList()}
        ref={dateRangeRef}
      >
        <span>{selectedRange}</span>
      </div>

      <div
        className="daterangepicker ltr show-ranges opensleft"
        style={pickerStyle}
      >
        <div className="ranges">
          <ul>
            {predefinedRanges.map((range) => (
              <li
                key={range.label}
                data-range-key={range.label}
                className={range.label === activeRange ? "active" : ""}
                onClick={() => handlePredefinedRange(range.range, range.label)}
              >
                {range.label}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="drp-calendar left"
          style={{ display: showCustomRange ? "block" : "none" }}
        >
          <div className="calendar-table">
            {renderCalendar(
              "calendar-left",
              "month-year-left",
              currentStartMonth,
              setCurrentStartMonth
            )}
          </div>
        </div>

        <div
          className="drp-calendar right"
          style={{ display: showCustomRange ? "block" : "none" }}
        >
          <div className="calendar-table">
            {renderCalendar(
              "calendar-right",
              "month-year-right",
              currentEndMonth,
              setCurrentEndMonth
            )}
          </div>
        </div>

        <div
          className="drp-buttons"
          style={{ display: showCustomRange ? "block" : "none" }}
        >
          <span className="drp-selected">{selectedRange}</span>
          <button className="cancelBtn btn btn-sm btn-default" type="button" onClick={() => showRange('none')}>
            Cancel
          </button>
          <button
            className="applyBtn btn btn-sm btn-primary"
            type="button"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </React.Fragment>
  );
};
export default EventCalendar;